import { useEffect, useState } from 'react';
import { STORE_INFO } from '@/mocks/content';

export default function Hero() {
  const [loaded, setLoaded] = useState(false);
  const [textVisible, setTextVisible] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setLoaded(true), 100);
    const t2 = setTimeout(() => setTextVisible(true), 800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <section className="relative w-full h-screen min-h-[600px] overflow-hidden flex items-center justify-center">
      {/* Background Image — Night Bloom animation */}
      <div
        className="absolute inset-0 transition-all duration-[2000ms] ease-out"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=luxury%20Japanese%20lounge%20bar%20interior%20dark%20elegant%20atmosphere%20warm%20amber%20gold%20lighting%20deep%20navy%20black%20background%20sophisticated%20night%20club%20bokeh%20soft%20glow%20premium%20establishment%20Toyama%20Japan%20moody%20cinematic%20wide%20angle&width=1920&height=1080&seq=hero001&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          filter: loaded ? 'blur(0px) brightness(0.7)' : 'blur(14px) brightness(0.3)',
          transform: loaded ? 'scale(1)' : 'scale(1.1)',
        }}
      />

      {/* Ambient Float — slow breathing scale */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=luxury%20Japanese%20lounge%20bar%20interior%20dark%20elegant%20atmosphere%20warm%20amber%20gold%20lighting%20deep%20navy%20black%20background%20sophisticated%20night%20club%20bokeh%20soft%20glow%20premium%20establishment%20Toyama%20Japan%20moody%20cinematic%20wide%20angle&width=1920&height=1080&seq=hero001&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          opacity: 0,
          animation: loaded ? 'ambientFloat 10s ease-in-out infinite alternate' : 'none',
        }}
      />

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0F]/40 via-[#0A0A0F]/20 to-[#0A0A0F]/80" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0F]/30 via-transparent to-[#0A0A0F]/30" />

      {/* Content */}
      <div className="relative z-10 w-full text-center px-6">
        <div
          className="transition-all duration-1000 ease-out"
          style={{
            opacity: textVisible ? 1 : 0,
            transform: textVisible ? 'translateY(0)' : 'translateY(-30px)',
            transitionDelay: '0ms',
          }}
        >
          <p
            className="text-[#C9A84C] tracking-[0.5em] text-xs md:text-sm mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          >
            {STORE_INFO.taglineEn}
          </p>
        </div>

        <div
          className="transition-all duration-1000 ease-out"
          style={{
            opacity: textVisible ? 1 : 0,
            transform: textVisible ? 'translateY(0)' : 'translateY(-20px)',
            transitionDelay: '200ms',
          }}
        >
          <h1
            className="text-white text-5xl md:text-7xl lg:text-8xl font-bold tracking-[0.2em] mb-6"
            style={{ fontFamily: "'Shippori Mincho', serif" }}
          >
            {STORE_INFO.name}
          </h1>
        </div>

        <div
          className="transition-all duration-1000 ease-out"
          style={{
            opacity: textVisible ? 1 : 0,
            transform: textVisible ? 'translateY(0)' : 'translateY(-10px)',
            transitionDelay: '400ms',
          }}
        >
          {/* Gold line */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className="w-16 md:w-24 h-px bg-[#C9A84C]/60" />
            <span
              className="text-[#C9A84C] text-xs tracking-[0.4em]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              LOUNGE &amp; SNACK
            </span>
            <span className="w-16 md:w-24 h-px bg-[#C9A84C]/60" />
          </div>
          <p
            className="text-white/80 text-base md:text-lg tracking-widest"
            style={{ fontFamily: "'Shippori Mincho', serif" }}
          >
            {STORE_INFO.tagline}
          </p>
        </div>

        <div
          className="transition-all duration-1000 ease-out mt-10"
          style={{
            opacity: textVisible ? 1 : 0,
            transform: textVisible ? 'translateY(0)' : 'translateY(10px)',
            transitionDelay: '600ms',
          }}
        >
          <a
            href={STORE_INFO.telHref}
            className="inline-flex items-center gap-3 px-8 py-3 border border-[#C9A84C]/70 text-[#C9A84C] text-sm tracking-widest hover:bg-[#C9A84C]/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
            style={{ fontFamily: "'Shippori Mincho', serif" }}
          >
            <i className="ri-phone-line" />
            お電話でご予約・空席確認
          </a>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 transition-opacity duration-1000"
        style={{ opacity: textVisible ? 1 : 0, transitionDelay: '1000ms' }}
      >
        <span
          className="text-[#C9A84C]/60 text-xs tracking-[0.4em]"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          SCROLL
        </span>
        <div className="w-px h-10 bg-gradient-to-b from-[#C9A84C]/60 to-transparent animate-pulse" />
      </div>

      <style>{`
        @keyframes ambientFloat {
          from { opacity: 0; transform: scale(1.0); }
          to   { opacity: 0.15; transform: scale(1.06); }
        }
      `}</style>
    </section>
  );
}
